/**
 * Transaction recovery utilities
 * Handles transaction errors and recovery after TON Connect SDK failures
 */

import { rpcCall } from "../ws/client";
import { toast } from "../components/ui/Toast";

export interface PendingTransaction {
    _id: string;
    type: string;
    wallet: string;
    metadata: any;
    status: string;
    created_at: string;
    tx_boc?: string;
    result?: any;
    refund_tx_hash?: string;
    refund_amount?: number;
    refund_at?: string;
}

/**
 * Check for pending transactions for the current wallet
 * Filters out completed transactions.
 */
export async function checkPendingTransactions(wallet: string): Promise<PendingTransaction[]> {
    try {
        const response = await rpcCall<{ wallet: string }, { pending_transactions: PendingTransaction[] }>(
            "tx.get_pending",
            { wallet }
        );

        if (response.result?.pending_transactions) {
            // Filter out completed transactions
            return response.result.pending_transactions.filter(
                tx => !["confirmed", "verified", "cancelled"].includes(tx.status)
            );
        }
    } catch (err) {
        console.error("Failed to check pending transactions:", err);
    }

    return [];
}

/**
 * Retry verification for a specific pending transaction
 * Returns true immediately if the transaction is already completed.
 */
export async function retryTransactionVerification(pendingTxId: string): Promise<boolean> {
    try {
        const response = await rpcCall<{ pending_tx_id: string }, { success: boolean; status: string }>(
            "tx.retry_verification",
            { pending_tx_id: pendingTxId }
        );

        // If the transaction is already completed, return true
        const status = response.result?.status;
        if (status === "confirmed" || status === "verified") {
            return true;
        }

        return response.result?.success || false;
    } catch (err) {
        console.error("Failed to retry transaction:", err);
        return false;
    }
}

/**
 * Show notification about pending transactions
 */
export function notifyPendingTransaction(pending: PendingTransaction) {
    const typeLabels: Record<string, string> = {
        "lottery_ticket": "билета лотереи",
        "escrow_deposit": "депозита в escrow",
        "jetton_purchase": "покупки жетонов"
    };
    
    const typeLabel = typeLabels[pending.type] || pending.type;
    
    toast.warning(
        `⏳ У вас есть незавершенная транзакция ${typeLabel}.\n\n` +
        `Транзакция отправлена, но подтверждение не получено.\n` +
        `Система автоматически проверит её статус в блокчейне.`,
        { duration: 8000 }
    );
}

/**
 * Handle transaction send error with recovery options
 */
export async function handleTransactionError(
    error: any,
    pendingTxId?: string,
    onSuccess?: () => void
): Promise<void> {
    console.error("Transaction error:", error);
    
    // Check if user rejected
    if (error?.message?.includes("reject") || error?.message?.includes("cancel") || 
        error?.message?.includes("User rejected")) {
        toast.warning("❌ Транзакция отменена пользователем");
        return;
    }
    
    // Check for validation errors (before sending) - these definitely didn't send
    if (
        error?.message?.includes("validation failed") ||
        error?.message?.includes("invalid 'payload'") ||
        error?.message?.includes("invalid 'address'") ||
        error?.message?.includes("invalid 'amount'")
    ) {
        toast.error(
            `❌ Ошибка в данных транзакции:\n${error.message}\n\n` +
            `Транзакция НЕ была отправлена. Обновите страницу и попробуйте снова.`,
            { duration: 8000 }
        );
        return;
    }
    
    // SDK errors or network errors after/during sending
    if (
        error?.message?.includes("Transaction was not sent") ||
        error?.message?.includes("rate limit") ||
        error?.message?.includes("TON_CONNECT_SDK_ERROR") ||
        error?.message?.includes("Network")
    ) {
        // Transaction might have been sent despite error
        toast.error(
            `⚠️ Ошибка при отправке: ${error.message}\n\n` +
            `Транзакция могла быть отправлена в блокчейн.\n` +
            `Система автоматически проверит её статус.\n\n` +
            `Обновите страницу через 30-60 секунд.`,
            { duration: 10000 }
        );
        
        // Start automatic verification if pending_tx_id provided
        if (pendingTxId) {
            setTimeout(async () => {
                const success = await retryTransactionVerification(pendingTxId);
                if (success) {
                    toast.success("✅ Транзакция подтверждена!");
                    onSuccess?.();
                }
            }, 5000); // Retry after 5 seconds
        }
        
        return;
    }
    
    // Other errors
    toast.error(error.message || "Не удалось выполнить транзакцию");
}

/**
 * Check and notify about pending transactions on component mount
 */
export async function checkAndNotifyPending(wallet: string | undefined) {
    if (!wallet) return;
    
    const pending = await checkPendingTransactions(wallet);
    
    if (pending.length > 0) {
        console.log(`Found ${pending.length} pending transactions:`);
        pending.forEach(tx => {
            console.log(`  - ${tx.type}: ${tx._id} (status: ${tx.status})`);
            notifyPendingTransaction(tx);
        });
    }
}
